import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Stack,
} from '@mui/material';
import {
  Home as HomeIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { API_URL } from '../../../utils/config';

interface Bill {
  id: string;
  vendorId: string;
  vendorName: string;
  docNumber?: string;
  transactionDate?: string;
  dueDate?: string;
  totalAmount?: number;
  balance?: number;
  currencyCode?: string;
  privateNote?: string;
  subsidiaryInvoiceNumber?: string;
  createdAt?: string;
  updatedAt?: string;
  invoice?: {
    id?: string;
    docNumber?: string;
    transactionDate?: string;
    customerId?: string;
    customerName?: string;
    totalAmount?: number;
    balance?: number;
    currencyCode?: string;
    currencyName?: string;
    status?: string;
    source?: string;
  } | null;
}


export const InvoiceBillManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { hasPermission } = useUserPermissions();

  const [allBills, setAllBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalBills, setTotalBills] = useState(0);
  const [selectedStatuses, setSelectedStatuses] = useState({
    open: true,
    payBill: true,
    paid: true,
    void: false,
  });

  // Fetch all vendor bills in a single call
  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/all-vendor-bills?page=${page + 1}&limit=${rowsPerPage}`
      );

      if (!response || !response.ok) {
        if (response?.status === 403) {
          throw new Error('Access denied. You need accounting.admin permission.');
        }
        throw new Error('Failed to load invoice/bill data');
      }

      const result = await response.json();
      setAllBills(result.data?.bills || []);
      setTotalBills(result.data?.pagination?.total || 0);
    } catch (err: any) {
      console.error('Error loading all data:', err);
      setError(err.message || 'Failed to load invoice/bill data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, [page, rowsPerPage]);

  const formatCurrency = (amount?: number, currency: string = 'USD') => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Determine the status of a bill
  const getBillStatus = (bill: Bill): 'payBill' | 'open' | 'paid' | 'void' | null => {
    // If bill has no invoice
    if (!bill.invoice) {
      // If bill balance is 0, it's paid
      if (bill.balance === 0) {
        return 'paid';
      }
      // Bills without invoices that have a balance cannot be classified
      return null;
    }

    // Check if invoice is void
    if (bill.invoice.status === 'void') {
      return 'void';
    }

    // Check if both invoice and bill are fully paid
    if (bill.invoice.status === 'paid' && bill.balance === 0) {
      return 'paid';
    }

    // Check if invoice is paid but bill still needs to be paid → PAY BILL
    if (bill.invoice.status === 'paid' && bill.balance !== 0 && bill.balance !== null && bill.balance !== undefined) {
      return 'payBill';
    }

    // Check if invoice balance is 0 but bill balance is not 0 → PAY BILL
    if (bill.invoice.balance === 0 && bill.balance !== 0 && bill.balance !== null && bill.balance !== undefined) {
      return 'payBill';
    }

    // Check if both balances are 0 → PAID
    if (bill.invoice.balance === 0 && bill.balance === 0) {
      return 'paid';
    }

    // Default to open if invoice status is open or unknown
    if (bill.invoice.status === 'open') {
      return 'open';
    }

    return null;
  };

  // Filter bills based on selected statuses
  const getFilteredBills = (): Bill[] => {
    return allBills.filter(bill => {
      const status = getBillStatus(bill);

      // If no status determined, skip this bill
      if (!status) {
        return false;
      }

      switch (status) {
        case 'payBill':
          return selectedStatuses.payBill;
        case 'open':
          return selectedStatuses.open;
        case 'paid':
          return selectedStatuses.paid;
        case 'void':
          return selectedStatuses.void;
        default:
          return false;
      }
    });
  };

  const filteredBills = getFilteredBills();

  // Calculate totals for filtered bills
  const calculateTotals = () => {
    let totalInvoiceBalance = 0;
    let totalBillBalance = 0;

    filteredBills.forEach(bill => {
      if (bill.invoice?.balance) {
        totalInvoiceBalance += bill.invoice.balance;
      }
      if (bill.balance) {
        totalBillBalance += bill.balance;
      }
    });

    return { totalInvoiceBalance, totalBillBalance };
  };

  const { totalInvoiceBalance, totalBillBalance } = calculateTotals();

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleStatusChange = (status: keyof typeof selectedStatuses) => {
    setSelectedStatuses(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
    setPage(0); // Reset to first page when filter changes
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, px: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, px: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4, px: 2 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        <Typography color="text.primary">Invoice/Bill Management</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
          <Typography variant="h4" component="h1">
            Invoice/Bill Management
          </Typography>
          <IconButton onClick={loadAllData} title="Refresh">
            <RefreshIcon />
          </IconButton>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View and manage invoices and bills across all vendors
        </Typography>
      </Box>

      {/* Bills Table */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 4 }}>
          {/* Left side: Filter by Status */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Filter by Status
            </Typography>
            <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedStatuses.open}
                    onChange={() => handleStatusChange('open')}
                  />
                }
                label="Open"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedStatuses.payBill}
                    onChange={() => handleStatusChange('payBill')}
                  />
                }
                label="Pay Bill"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedStatuses.paid}
                    onChange={() => handleStatusChange('paid')}
                  />
                }
                label="Paid"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedStatuses.void}
                    onChange={() => handleStatusChange('void')}
                  />
                }
                label="Void"
              />
            </Stack>
          </Box>

          {/* Right side: Totals */}
          <Box sx={{ textAlign: 'right', minWidth: '250px' }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Total Outstanding Invoice Balance
              </Typography>
              <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 700 }}>
                {formatCurrency(totalInvoiceBalance)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Total Outstanding Bill Balance
              </Typography>
              <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 700 }}>
                {formatCurrency(totalBillBalance)}
              </Typography>
            </Box>
          </Box>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vendor Name</TableCell>
                <TableCell>HHTL Invoice #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Invoice Amount</TableCell>
                <TableCell align="right">Invoice Balance</TableCell>
                <TableCell>Invoice Currency</TableCell>
                <TableCell align="center" sx={{ width: 40 }}></TableCell>
                <TableCell>HHTL Bill #</TableCell>
                <TableCell>Subsidiary Inv#</TableCell>
                <TableCell align="right">Bill Amount</TableCell>
                <TableCell align="right">Bill Balance</TableCell>
                <TableCell>Bill Currency</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBills
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((bill) => (
                <TableRow key={bill.id}>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate(`/admin/hub/accounting/vendors/${bill.vendorId}`)}
                      sx={{ textAlign: 'left' }}
                    >
                      {bill.vendorName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {bill.invoice?.docNumber ? (
                      <Chip
                        label={bill.invoice.docNumber}
                        size="small"
                        clickable
                        onClick={() => navigate(`/admin/hub/accounting/invoices/${bill.invoice?.id}`)}
                        sx={{
                          fontFamily: 'monospace',
                          backgroundColor: 'rgba(0, 45, 96, 0.08)',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 45, 96, 0.15)',
                          }
                        }}
                      />
                    ) : (
                      <Typography variant="body2">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {bill.invoice?.transactionDate ? formatDate(bill.invoice.transactionDate) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {bill.invoice?.customerId && bill.invoice?.customerName ? (
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/admin/hub/accounting/customers/${bill.invoice?.customerId}`)}
                        sx={{ textAlign: 'left' }}
                      >
                        {bill.invoice.customerName}
                      </Link>
                    ) : (
                      <Typography variant="body2">
                        {bill.invoice?.customerName || '—'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {bill.invoice?.totalAmount !== undefined ?
                        formatCurrency(bill.invoice.totalAmount, bill.invoice?.currencyCode || bill.currencyCode) :
                        '—'
                      }
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {bill.invoice?.balance !== undefined ? (
                      <Typography
                        variant="body2"
                        sx={{
                          color: bill.invoice.balance === 0 ? 'green' :
                                 bill.invoice.balance > 0 ? 'error.main' :
                                 'text.primary'
                        }}
                      >
                        {formatCurrency(bill.invoice.balance, bill.invoice?.currencyCode || bill.currencyCode)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {bill.invoice?.currencyName || bill.currencyCode || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 40 }}>
                    {bill.invoice && (
                      <Tooltip title="Bill linked to Invoice">
                        <LinkIcon sx={{ fontSize: 18 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bill.id}
                      size="small"
                      clickable
                      onClick={() => navigate(`/admin/hub/accounting/bills/${bill.id}`)}
                      sx={{
                        fontFamily: 'monospace',
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.15)',
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {bill.subsidiaryInvoiceNumber ? (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {bill.subsidiaryInvoiceNumber}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatCurrency(bill.totalAmount, bill.currencyCode)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        color: bill.balance === 0 ? 'green' :
                               bill.balance && bill.balance > 0 ? 'error.main' :
                               'text.primary'
                      }}
                    >
                      {formatCurrency(bill.balance, bill.currencyCode)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {bill.currencyCode || 'USD'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(bill.invoice?.balance === 0 && bill.balance !== 0 && bill.balance !== null && bill.balance !== undefined) ? (
                      <Chip
                        label="PAY BILL"
                        size="small"
                        color="error"
                        sx={{ fontWeight: 600 }}
                      />
                    ) : bill.invoice?.status ? (
                      <Chip
                        label={bill.invoice.status.toUpperCase()}
                        size="small"
                        color={
                          bill.invoice.status === 'paid' ? 'success' :
                          bill.invoice.status === 'void' ? 'default' :
                          'warning'
                        }
                        sx={{ fontWeight: 600 }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filteredBills.length === 0 && (
                <TableRow>
                  <TableCell colSpan={14} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No business found matching the selected statuses
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredBills.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};
